<template>
  <oxd-dialog
    :style="{width: '90%', maxWidth: '1050px'}"
    @update:show="onCancel"
    ><div class="gsl-hrm-modal-header">
      <oxd-text type="card-title">
        {{ $t('general.budget_calculation') }}
      </oxd-text>
    </div>
    <oxd-divider />
    <oxd-form :loading="isLoading">
      <oxd-form-row>
        <oxd-grid :cols="2" class="gsl-hrm-timesheet-grid">
          <oxd-text tag="p" class="gsl-hrm-timesheet-title">
            {{ $t('time.project') }}:
          </oxd-text>
          <oxd-text tag="p" class="gsl-hrm-timesheet-text">
            {{ data.projectName }}
          </oxd-text>
          <oxd-text tag="p" class="gsl-hrm-timesheet-title">
            {{ $t('general.date') }}:
          </oxd-text>
          <oxd-text tag="p" class="gsl-hrm-timesheet-text">
            {{ data.fromDate }} - {{ chosenEndDate }}
          </oxd-text>

          <oxd-text tag="p" class="gsl-hrm-timesheet-title">
            {{ $t('general.total_budgeted_cost') }}:
          </oxd-text>
          <oxd-text tag="p" class="gsl-hrm-timesheet-text">
            {{ data.currency }} {{ totalAllocationCost }}
          </oxd-text>
          <oxd-text tag="p" class="gsl-hrm-timesheet-title">
            {{ $t('general.budgeted_cost_till_date') }}:
          </oxd-text>
          <oxd-text tag="p" class="gsl-hrm-timesheet-text">
            {{ data.currency }} {{ totalBudgetedCost }}
          </oxd-text>
        </oxd-grid>
      </oxd-form-row>
      <oxd-divider />
      <div class="gsl-hrm-container">
        <table class="gsl-hrm-timesheet-table">
          <thead class="gsl-hrm-timesheet-table-header">
            <tr class="gsl-hrm-timesheet-table-header-row">
              <th class="gsl-hrm-timesheet-table-header-cell --center">
                <span>{{ $t('general.role') }}</span>
              </th>
              <th class="gsl-hrm-timesheet-table-header-cell --center">
                <span>{{ $t('general.head_count') }}</span>
              </th>
              <th class="gsl-hrm-timesheet-table-header-cell --center">
                <span>{{ $t('general.budgeted_cost') }}</span>
              </th>

              <th class="gsl-hrm-timesheet-table-header-cell --center">
                <span>{{ $t('general.allocation_cost') }}</span>
              </th>
              <th class="gsl-hrm-timesheet-table-header-cell --center">
                <span>{{ $t('general.actual_cost') }}</span>
              </th>
              <th class="gsl-hrm-timesheet-table-header-cell --center">
                <span>{{ $t('general.variance') }}</span>
              </th>
            </tr>
          </thead>
          <tbody class="gsl-hrm-timesheet-table-body">
            <tr
              v-for="(record, index) in budgetRecords"
              :key="index"
              class="gsl-hrm-timesheet-table-body-row"
            >
              <td class="gsl-hrm-timesheet-table-body-cell --center">
                {{ record.role }}
              </td>
              <td class="gsl-hrm-timesheet-table-body-cell --center">
                {{ record.headCount }}
              </td>
              <td class="gsl-hrm-timesheet-table-body-cell --center">
                {{ record.cost }}
              </td>
              <td class="gsl-hrm-timesheet-table-body-cell --center">
                {{ record.actualCost }}
              </td>
              <td class="gsl-hrm-timesheet-table-body-cell --center">
                {{ record.budgetedCost }}
              </td>
              <td
                class="gsl-hrm-timesheet-table-body-cell --center"
                :style="getVarianceColor(record.variance)"
              >
                {{ record.variance }}
              </td>
            </tr>
          </tbody>
          <tfoot class="gsl-hrm-timesheet-table-footer">
            <tr class="gsl-hrm-timesheet-table-footer-row">
              <td class="gsl-hrm-timesheet-table-footer-cell --center">
                {{ $t('general.total') }}
              </td>
              <td></td>
              <td class="gsl-hrm-timesheet-table-body-cell --center">
                {{ data.currency }} {{ totalAllocationCost }}
              </td>
              <td class="gsl-hrm-timesheet-table-body-cell --center">
                {{ data.currency }} {{ totalActualCost }}
              </td>
              <td class="gsl-hrm-timesheet-table-body-cell --center">
                {{ data.currency }} {{ totalBudgetedCost }}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <br />
      <oxd-form-actions>
        <oxd-button
          type="button"
          display-type="ghost"
          :label="$t('general.cancel')"
          @click="onCancel"
        />
      </oxd-form-actions>
    </oxd-form>
  </oxd-dialog>
</template>

<script>
import {onBeforeMount, ref} from 'vue';
import {OxdDialog} from '@ohrm/oxd';
import {APIService} from '@/core/util/services/api.service';

export default {
  name: 'ProjectBudgetCostModel',
  components: {
    'oxd-dialog': OxdDialog,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
    projectId: {
      type: Number,
      required: true,
    },
  },
  emits: ['close'],
  setup(props) {
    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/time/project/${props.projectId}/project-budget`,
    );

    const timesheetItemDuration = ref([]);

    const now = new Date();

    const isLoading = ref(true);
    const budgetRecords = ref([]);

    const calculateBudgetedCost = (record, data) => {
      // You can either duplicate the logic here
      if (!data || !Array.isArray(data.items)) {
        return 0;
      }
      let ratePerDay;
      if (record.period === 'day') ratePerDay = record.cost;
      else if (record.period === 'week') ratePerDay = record.cost / 5;
      else if (record.period === 'month') ratePerDay = record.cost / 21;
      else if (record.period === 'year') ratePerDay = record.cost / 260;
      else return 'N/A';

      const totalCost = data.items
        .filter((item) => item.roleId == record.role.id)
        .reduce((sum, item) => {
          const daysWorked = item.totalDurationHours / data.lengthHour;
          return sum + daysWorked * record.headCount * ratePerDay;
        }, 0);

      return totalCost;
    };

    const calculateActualCost = (record, newWorkingDays = null) => {
      // You can either duplicate the logic here

      const workingDays = newWorkingDays || props?.data?.workingDays;
      if (!workingDays) return null;

      let ratePerDay;
      if (record.period === 'day') ratePerDay = record.cost;
      else if (record.period === 'week') ratePerDay = record.cost / 5;
      else if (record.period === 'month') ratePerDay = record.cost / 21;
      else if (record.period === 'year') ratePerDay = record.cost / 260;
      else return 'N/A';

      const totalCost = workingDays * record.headCount * ratePerDay;
      return totalCost;
    };

    const calculateWeekdaysBetweenDates = (fromDate, toDate) => {
      let count = 0;

      // Ensure both dates are Date objects and clone the start date
      const start = new Date(fromDate);
      const end = new Date(toDate);
      const currentDate = new Date(start);

      while (currentDate <= end) {
        const day = currentDate.getDay();
        if (day !== 0 && day !== 6) {
          count++; // Weekday (Mon-Fri)
        }
        currentDate.setDate(currentDate.getDate() + 1);
      }

      return count;
    };

    const fetchDaysOfWorked = async () => {
      if (props.data.fromDate) {
        const response = await http.request({
          method: 'GET',
          url: '/api/v2/time/timesheet/project',
          params: {
            projectId: props.projectId,
            fromDate: props.data.fromDate,
            toDate: now.toISOString().split('T')[0],
          },
        });

        timesheetItemDuration.value = response?.data?.data;
      }
    };

    // fetchDaysOfWorked();

    const loadBudget = async () => {
      try {
        const {data} = await http.getAll();
        if (data.data && data.data.length > 0) {
          const records = data.data.map((item) => {
            const budgetedCost = parseFloat(
              calculateBudgetedCost(item, timesheetItemDuration.value),
            );
            const actualCost = parseFloat(
              calculateActualCost(
                item,
                calculateWeekdaysBetweenDates(
                  props?.data.fromDate,
                  now.toISOString().split('T')[0],
                ),
              ),
            );

            const variance =
              budgetedCost !== 0
                ? Math.max(
                    0,
                    ((budgetedCost - actualCost) / budgetedCost) * 100,
                  )
                : 0;

            return {
              id: item.id,
              role: item.role.name,
              headCount: item.headCount,
              cost: `${props.data.currency} ${calculateActualCost(
                item,
              ).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`,
              actualCost: `${props.data.currency} ${actualCost?.toLocaleString(
                undefined,
                {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                },
              )}`,
              budgetedCost: `${
                props.data.currency
              } ${budgetedCost?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`,
              variance: `${variance.toFixed(2)}%`,
            };
          });

          budgetRecords.value = records;
        }
      } catch (error) {
        console.error('Error loading budget:', error);
      } finally {
        isLoading.value = false;
      }
    };

    onBeforeMount(async () => {
      await fetchDaysOfWorked();
      await loadBudget(); // Trigger data loading once before the component is mounted
    });
    return {
      http,
      isLoading,
      budgetRecords,
      timesheetItemDuration,
      calculateBudgetedCost,
      calculateActualCost,
      calculateWeekdaysBetweenDates,
    };
  },

  computed: {
    totalBudgetedCost() {
      return this.budgetRecords
        .reduce((sum, r) => {
          const value = parseFloat(r.budgetedCost.replace(/[^\d.-]/g, ''));
          return sum + (isNaN(value) ? 0 : value);
        }, 0)
        .toLocaleString(undefined, {minimumFractionDigits: 2});
    },
    totalActualCost() {
      return this.budgetRecords
        .reduce((sum, r) => {
          const value = parseFloat(r.actualCost.replace(/[^\d.-]/g, ''));
          return sum + (isNaN(value) ? 0 : value);
        }, 0)
        .toLocaleString(undefined, {minimumFractionDigits: 2});
    },
    totalAllocationCost() {
      return this.budgetRecords
        .reduce((sum, r) => {
          const value = parseFloat(r.cost.replace(/[^\d.-]/g, ''));
          return sum + (isNaN(value) ? 0 : value);
        }, 0)
        .toLocaleString(undefined, {minimumFractionDigits: 2});
    },

    chosenEndDate() {
      const today = new Date();
      return today.toISOString().split('T')[0];
    },
  },

  methods: {
    onCancel() {
      this.$emit('close');
    },
    getVarianceColor(value) {
      const numericVariance = parseFloat(value.replace('%', ''));
      return {
        color: numericVariance == 0 ? 'green' : 'red',
        fontWeight: 'bold',
      };
    },
  },
};
</script>
<style lang="scss" scoped>
.gsl-hrm-timesheet {
  &-grid {
    width: 100%;
    padding: 0 0.625rem;
    display: grid;
    grid-template-columns: 300px 1fr;
    gap: 0.5rem;
    margin-bottom: 1rem;

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  &-title,
  &-text {
    word-break: break-word;
    font-size: $oxd-input-control-font-size;
  }

  &-title {
    font-weight: 700;
  }

  &-table {
    width: 100%;
    border-collapse: separate;
    font-family: $oxd-font-family;
    color: $oxd-input-control-font-color;
    font-size: $oxd-input-control-font-size;
    font-weight: $oxd-input-control-font-weight;
    border-spacing: 0 5px;
    overflow-x: auto;
    @media (max-width: 950px) {
      display: block;
    }
    thead {
      .gsl-hrm-timesheet-table-header-row {
        th {
          text-align: center;
          font-weight: 600;
          color: #333;
          white-space: nowrap;
        }
      }
    }

    tbody {
      .gsl-hrm-timesheet-table-body-row {
        background-color: $oxd-white-color;

        td {
          text-align: center;
          padding: 0.75rem 1rem;
          font-size: 14px;
          word-break: break-word;
          white-space: nowrap;

          &:first-child {
            border-top-left-radius: 20px;
            border-bottom-left-radius: 20px;
          }
          &:last-child {
            border-top-right-radius: 20px;
            border-bottom-right-radius: 20px;
          }
        }
      }
    }
    tfoot {
      .gsl-hrm-timesheet-table-footer-row {
        td {
          text-align: center;
          font-weight: 600;
          color: #333;
          white-space: nowrap;
          padding: 0.3rem;
        }
      }
    }
  }
}

// Make modal responsive
</style>
